import { type ComponentProps, useCallback } from "react";
import { type OptionProps } from "@root/shared/components/SelectBox";
import { type Employment } from "@userFrontend/features/clientReview/components/ClientReviewInputs/uiParts/Employment";
import { type TreatmentAndCulture } from "@userFrontend/features/clientReview/components/ClientReviewInputs/uiParts/TreatmentAndCulture";
import { type WorkplaceReviewFeature } from "@userFrontend/features/clientReview/components/ClientReviewInputs/uiParts/WorkplaceReviewFeature";
import { type WorkplaceReviewThought } from "@userFrontend/features/clientReview/components/ClientReviewInputs/uiParts/WorkplaceReviewThought";
import { type DecidingFactors } from "@userFrontend/features/clientReview/components/ClientReviewInputs/uiParts/DecidingFactors";
import { type Satisfaction } from "@root/userFrontend/features/clientReview/components/ClientReviewInputs/uiParts/Satisfaction";
// utils
import { type SiteId } from "@root/shared/types/type";
import { PlaceholderText } from "./utils/placeholderText";
// types
import {
  type OccupationsProps,
  type EmploymentsProps,
  type BusinessTypesProps,
  type SelectOptionQuestionsProps,
  type TextAreaQuestionsProps,
} from "./type";

type UseFormatQuestionParams = {
  siteId: SiteId;
  occupations: OccupationsProps;
  employments: EmploymentsProps;
  businessTypes: BusinessTypesProps;
  selectiveQuestions: SelectOptionQuestionsProps;
  textAreaQuestions: TextAreaQuestionsProps;
  satisfactionRateOptions: OptionProps[];
  reasonsOptions: OptionProps[];
};

export type FormattedQuestions = {
  employmentQuestions: ComponentProps<typeof Employment>["questions"];
  treatmentAndCultureQuestions: ComponentProps<typeof TreatmentAndCulture>["questions"];
  workplaceReviewFeatureQuestions: ComponentProps<typeof WorkplaceReviewFeature>["questions"];
  workplaceReviewThoughtQuestions: ComponentProps<typeof WorkplaceReviewThought>["questions"];
  decidingFactorsQuestions: ComponentProps<typeof DecidingFactors>["questions"];
  satisfactionAndThoughtsQuestions: ComponentProps<typeof Satisfaction>["questions"];
};

export const useFormatQuestion = (params: UseFormatQuestionParams): FormattedQuestions => {
  const {
    siteId,
    occupations,
    employments,
    businessTypes,
    selectiveQuestions,
    textAreaQuestions,
    satisfactionRateOptions,
    reasonsOptions,
  } = params;

  const siteKey = siteId === 1 ? "biyo" : "kaigo";

  const formatQuestions = useCallback((): FormattedQuestions => {
    const businessTypesSelectOptions: OptionProps[] = (businessTypes[siteKey] || []).map(
      (businessType) => ({
        label: businessType.businessTypeName,
        value: businessType.businessTypeId,
      }),
    );

    const occupationsSelectOptions: OptionProps[] = occupations.map((occupation) => ({
      label: occupation.occupationName,
      value: occupation.occupationId,
      businessTypeIds: occupation.businessTypeIds,
    }));

    const employmentsSelectOptions: OptionProps[] = employments.map((employment) => ({
      label: employment.employmentName,
      value: employment.employmentId,
    }));

    const continuedServiceYearsSelectOptions: OptionProps[] =
      selectiveQuestions.continuedServiceYears[siteKey].map((continuedServiceYear) => ({
        label: continuedServiceYear.optionText,
        value: continuedServiceYear.optionId,
      }));

    const employmentQuestions = {
      occupationsSelectOptions,
      employmentsSelectOptions,
      employmentStatus: selectiveQuestions.employmentStatus[siteKey],
      continuedServiceYearsSelectOptions,
      businessTypesSelectOptions,
    };

    const treatmentAndCultureQuestions = {
      treatmentQuestions: selectiveQuestions.treatments[siteKey],
      cultureQuestions: selectiveQuestions.cultures[siteKey],
    };

    const workplaceReviewFeatureQuestions = {
      atmosphere: selectiveQuestions.atmosphere[siteKey],
      salaryStandard: selectiveQuestions.salaryStandard[siteKey],
      debut: selectiveQuestions.debut[siteKey],
      education: selectiveQuestions.education[siteKey],
      workTime: selectiveQuestions.workTime[siteKey],
      salaryChange: selectiveQuestions.salaryChange[siteKey],
    };

    const workplaceReviewThoughtQuestions = {
      workplaceMatching: {
        ...textAreaQuestions.workplaceMatching[siteKey][0],
        placeholder: PlaceholderText.workplaceMatching,
      },
      interestReward: {
        ...textAreaQuestions.interestReward[siteKey][0],
        placeholder: PlaceholderText.interestReward,
      },
      challenge: {
        ...textAreaQuestions.challenge[siteKey][0],
        placeholder: PlaceholderText.challenge,
      },
      expectation: {
        ...textAreaQuestions.expectation[siteKey][0],
        placeholder: PlaceholderText.expectation,
      },
    };

    const satisfactionAndThoughtsQuestions = {
      rate: {
        selected: false,
        options: satisfactionRateOptions,
      },
      reason: reasonsOptions.map((option, index) => ({
        index,
        optionId: Number(option.value),
        optionText: option.label,
      })),
    };

    const decidingFactorsQuestions = selectiveQuestions.decidingFactors?.[siteKey] || [];

    return {
      employmentQuestions,
      treatmentAndCultureQuestions,
      workplaceReviewFeatureQuestions,
      workplaceReviewThoughtQuestions,
      decidingFactorsQuestions,
      satisfactionAndThoughtsQuestions,
    };
  }, [
    siteKey,
    occupations,
    employments,
    businessTypes,
    selectiveQuestions,
    textAreaQuestions,
    satisfactionRateOptions,
    reasonsOptions,
  ]);

  return formatQuestions();
};
